import React, { useRef, useState } from 'react';

import { Button } from 'primereact/button';
import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect';
import { Toast } from 'primereact/toast';
import {
  TriStateCheckbox,
  TriStateCheckboxChangeEvent
} from 'primereact/tristatecheckbox';
import { Nullable } from 'primereact/ts-helpers';

import { AssetTemplate } from '../../constants';
import { Asset, DropdownSelectorType } from '../../types/interfaces';
import TablePageHeader from '../../ui/shared/TablePageHeader';
import { TagsComponent } from '../../ui/TagsComponent';
import { PickerTexts } from '../../utils';

import AssetsMobileFiltersDialog from './AssetsMobileFiltersDialog';

interface AssetsTableHeaderProps {
  selectedTypeFilter: DropdownSelectorType[];
  assetTypesFilterOptions: DropdownSelectorType[];
  handleMultiselectChange: (e: MultiSelectChangeEvent) => void;
  activeFilterActive: Nullable<boolean>;
  setActiveFilterActive: (value: Nullable<boolean>) => void;
  handleInputChange: (
    filter: string
  ) => (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleDeleteTaskStatus: (taskId: string | number) => void;
  exportTemplate: () => void;
  importAssetsXLS: (e: React.ChangeEvent<HTMLInputElement>) => void;
  exportExcel: () => void;
  setSelectedAssets: React.Dispatch<React.SetStateAction<Asset>>;
  setDialogVisible: React.Dispatch<React.SetStateAction<boolean>>;
  toast: React.MutableRefObject<Toast | null>;
  isSelectedIdExist: boolean;
  setAssetToDelete: React.Dispatch<
    React.SetStateAction<string | number | null>
  >;
  setDeleteDialogVisible: React.Dispatch<React.SetStateAction<boolean>>;
  selectedAssets: Asset;
  hasHorizontalScroll: boolean;
  isMobile: boolean;
}

const AssetsTableHeader: React.FC<AssetsTableHeaderProps> = ({
  selectedTypeFilter,
  assetTypesFilterOptions,
  handleMultiselectChange,
  activeFilterActive,
  setActiveFilterActive,
  handleInputChange,
  handleDeleteTaskStatus,
  exportTemplate,
  importAssetsXLS,
  exportExcel,
  setSelectedAssets,
  setDialogVisible,
  toast,
  isSelectedIdExist,
  setAssetToDelete,
  setDeleteDialogVisible,
  selectedAssets,
  hasHorizontalScroll,
  isMobile
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [mobileFiltersVisible, setMobileFiltersVisible] = useState(false);

  const onAdd = () => {
    setSelectedAssets(AssetTemplate);
    setDialogVisible(true);
  };

  const onEdit = () => {
    if (toast.current && !isSelectedIdExist) {
      toast.current.show({
        severity: 'warn',
        summary: 'Ошибка',
        detail: 'Выберите объект для редактирования',
        life: 3000
      });
    }
    if (isSelectedIdExist) setDialogVisible(true);
  };

  const onDelete = () => {
    if (isSelectedIdExist) {
      setAssetToDelete(selectedAssets.id);
      setDeleteDialogVisible(true);
    }
    if (toast.current && !isSelectedIdExist) {
      toast.current.show({
        severity: 'warn',
        summary: 'Ошибка',
        detail: 'Выберите объект для удаления',
        life: 3000
      });
    }
  };

  const handleApplyMobileFilters = (filters: {
    selectedTypes: DropdownSelectorType[];
    active: Nullable<boolean>;
  }) => {
    const mockEvent = {
      value: filters.selectedTypes
    } as MultiSelectChangeEvent;
    handleMultiselectChange(mockEvent);
    setActiveFilterActive(filters.active);
  };

  const desktopButtonsLeft = [
    <div key="asset-type-filter" className="asset-type-filter">
      <MultiSelect
        value={selectedTypeFilter}
        options={assetTypesFilterOptions}
        onChange={handleMultiselectChange}
        optionLabel="label"
        fixedPlaceholder
        placeholder={PickerTexts.objectTypes(selectedTypeFilter.length)}
        filter
        className={
          selectedTypeFilter.length < 1
            ? 'p-invalid assets-header__MultiSelect'
            : 'assets-header__MultiSelect'
        }
        panelClassName="assets-header__customMultiSelect"
        itemClassName="assets-header__customMultiSelect"
      />
    </div>,
    <div
      key="asset-filters__active"
      className="asset-filters__active flex gap-2 align-items-center"
    >
      <TriStateCheckbox
        id="activeFilterToggle"
        value={activeFilterActive}
        onChange={(e: TriStateCheckboxChangeEvent) => {
          setActiveFilterActive(e.value);
        }}
      />
      <div style={{ color: '#6c757d' }} className="my-2 font-semibold">
        Активные объекты
      </div>
    </div>
  ];

  const mobileButtonsLeft = [
    <div key={`tasks-map-wrapper-${hasHorizontalScroll}`}>
      <Button
        key="mobile-filters-toggle"
        type="button"
        icon="pi pi-sliders-h"
        rounded
        severity="secondary"
        onClick={() => setMobileFiltersVisible(true)}
        tooltipOptions={{ position: 'top' }}
      />
    </div>
  ];

  const mobileButtonsRight = [
    <input
      key="input"
      type="file"
      accept="csv"
      onChange={importAssetsXLS}
      style={{ visibility: 'hidden', width: '0' }}
      ref={fileInputRef}
    />,
    <Button
      key="export-template-button"
      className="assets-import-btn__roles"
      type="button"
      severity="secondary"
      icon="pi pi-file-excel"
      rounded
      onClick={exportTemplate}
      tooltip="Выгрузка актуального шаблона импорта"
      tooltipOptions={{ position: hasHorizontalScroll ? 'left' : 'top' }}
    />,
    <Button
      key="import-button"
      className="assets-import-btn__roles"
      type="button"
      severity="secondary"
      icon="pi pi-file-import"
      rounded
      onClick={() => {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        fileInputRef.current?.click();
      }}
      label={isMobile ? undefined : 'Импорт'}
    />,
    <Button
      key="export-button"
      className="assets-export-btn__roles"
      type="button"
      severity="secondary"
      icon="pi pi-file-export"
      rounded
      onClick={exportExcel}
      tooltipOptions={{ position: hasHorizontalScroll ? 'left' : 'top' }}
      label={isMobile ? undefined : 'Экспорт'}
    />
  ];

  const desktopButtonsRight = [
    <input
      key="input"
      type="file"
      accept="csv"
      onChange={importAssetsXLS}
      style={{ visibility: 'hidden', width: '0' }}
      ref={fileInputRef}
    />,
    <Button
      key="export-template-button"
      className="assets-import-btn__roles"
      type="button"
      severity="secondary"
      icon="pi pi-file-excel"
      rounded
      onClick={exportTemplate}
      tooltip="Выгрузка актуального шаблона импорта"
      tooltipOptions={{ position: hasHorizontalScroll ? 'left' : 'top' }}
    />,
    <Button
      key="import-button"
      className="assets-import-btn__roles"
      type="button"
      severity="secondary"
      icon="pi pi-file-import"
      rounded
      onClick={() => {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        fileInputRef.current?.click();
      }}
      label={isMobile ? undefined : 'Импорт'}
    />,
    <Button
      key="export-button"
      className="assets-export-btn__roles"
      type="button"
      severity="secondary"
      icon="pi pi-file-export"
      rounded
      onClick={exportExcel}
      tooltipOptions={{ position: hasHorizontalScroll ? 'left' : 'top' }}
      label={isMobile ? undefined : 'Экспорт'}
    />
  ];

  return (
    <>
      <TablePageHeader
        tableName="assets"
        onGlobalSearchChange={(newValue) => {
          const mockEvent = {
            target: { value: newValue }
          } as React.ChangeEvent<HTMLInputElement>;
          handleInputChange('global')(mockEvent);
        }}
        hasHorizontalScroll={hasHorizontalScroll}
        customButtonsLeft={isMobile ? mobileButtonsLeft : desktopButtonsLeft}
        customButtonsRight={isMobile ? mobileButtonsRight : desktopButtonsRight}
        onAdd={onAdd}
        onEdit={onEdit}
        onDelete={onDelete}
        additionalLine={
          selectedTypeFilter.length > 0 && !isMobile ? (
            <div
              style={{ width: '100%', maxHeight: '150px', overflow: 'auto' }}
              id="tags-scroll"
            >
              <TagsComponent
                initialTagsItems={selectedTypeFilter}
                onDelete={handleDeleteTaskStatus}
              />
            </div>
          ) : undefined
        }
        isMobile={isMobile}
      />

      <AssetsMobileFiltersDialog
        visible={mobileFiltersVisible}
        onHide={() => setMobileFiltersVisible(false)}
        selectedTypeFilter={selectedTypeFilter}
        assetTypesFilterOptions={assetTypesFilterOptions}
        activeFilterActive={activeFilterActive}
        onApply={handleApplyMobileFilters}
        onDeleteTag={handleDeleteTaskStatus}
        isMobile={isMobile}
      />
    </>
  );
};

export default AssetsTableHeader;
